import React from 'react';
import classNames from 'classnames';
import { ArrowRight, MapPin } from 'lucide-react';

import { useConfiguration } from '../../../context/configurationContext';
import { NamedLink } from '../../../components';
import { createSlug } from '../../../util/urlHelpers';
import { formatPublicLocation } from '../../../util/listingLocationDisplay';

import { useFeaturedListings } from '../../../hooks/useFeaturedListings';

type SpotlightDealSectionProps = {
  sectionId: string;
  className?: string;
  title?: { content?: string } | null;
  description?: { content?: string } | null;
  options?: any;
  allSections?: any[];
};

const formatPriceCents = (amount?: number, currency?: string) =>
  amount != null
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        minimumFractionDigits: 0,
      }).format(amount / 100)
    : null;

type SpotlightCardProps = {
  listing: any;
  categories: Array<{ id: string; name: string }>;
};

/**
 * Single spotlight card. Kept as its own component so the section can later
 * iterate over multiple deals in a carousel without refactoring layout.
 */
const SpotlightDealCard = ({ listing, categories }: SpotlightCardProps) => {
  const id = listing?.id?.uuid;
  const attrs = listing?.attributes || {};
  const { title = '', description, price, publicData = {} } = attrs;
  const slug = createSlug(title);

  const firstImage = listing?.images?.[0];
  const imgVariants = firstImage?.attributes?.variants || {};
  const imgUrl =
    imgVariants['scaled-medium']?.url ||
    imgVariants['listing-card-2x']?.url ||
    imgVariants['listing-card']?.url ||
    '';

  const { amount, currency } = price || {};
  const priceLabel = formatPriceCents(amount, currency);
  const retailLabel =
    formatPriceCents(publicData.retailPrice, currency) ||
    formatPriceCents(publicData.oldPrice, currency);

  const locationDisplay = formatPublicLocation(publicData.location);
  const categoryId = publicData.categoryLevel1 || '';
  const categoryConfig = categories.find(c => c.id === categoryId);
  const categoryDisplay = categoryConfig?.name || categoryId;

  return (
    <div
      className="relative overflow-hidden rounded-[28px] border border-border/80 bg-card shadow-[0_22px_70px_-34px_rgba(15,23,42,0.45)]"
      data-testid={`card-spotlight-${id}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted lg:aspect-auto">
          {imgUrl ? (
            <img
              src={imgUrl}
              alt={title}
              className="h-full w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="h-full w-full bg-muted" />
          )}
          <div className="absolute left-6 top-6 z-10">
            <span className="inline-flex items-center rounded-md bg-primary px-3 py-2 font-heading text-xs font-bold uppercase tracking-[0.18em] text-primary-foreground shadow-md">
              Spotlight Deal
            </span>
          </div>
        </div>

        <div className="flex flex-col justify-between gap-6 p-8 lg:p-12">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3 text-xs">
              {categoryDisplay ? (
                <span className="inline-flex items-center rounded-md border border-border bg-background px-3 py-1.5 font-heading uppercase tracking-[0.16em] text-foreground">
                  {categoryDisplay}
                </span>
              ) : null}
              {locationDisplay ? (
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" />
                  {locationDisplay}
                </span>
              ) : null}
            </div>

            <h2 className="font-heading text-3xl font-bold uppercase leading-tight tracking-tight md:text-4xl">
              {title}
            </h2>

            {description ? (
              <p className="text-base leading-relaxed text-muted-foreground">{description}</p>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-baseline gap-3">
              {priceLabel ? (
                <span className="font-heading text-5xl font-bold leading-none text-green-600">
                  {priceLabel}
                </span>
              ) : null}
              {retailLabel ? (
                <span className="text-base text-muted-foreground line-through">
                  Retail: {retailLabel}
                </span>
              ) : null}
            </div>

            <NamedLink
              name="ListingPage"
              params={{ id, slug }}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-6 py-4 font-heading text-base font-bold uppercase tracking-[0.16em] text-primary-foreground transition-colors hover:bg-primary/90 sm:w-auto"
            >
              View Listing <ArrowRight className="h-5 w-5" />
            </NamedLink>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Spotlight Deal: operator-curated featured listing rendered in a hero card.
 * Wired through the shared `featuredListings` pipeline like every other
 * listing section. Currently shows the first matching listing; the component
 * already iterates over `listings`, so swapping in a carousel later is a
 * one-component change rather than a rewrite.
 */
const SpotlightDealSection = (props: SpotlightDealSectionProps) => {
  const { sectionId, className, title, description, options, allSections } = props;
  const config = useConfiguration() as any;
  const categories = config?.categoryConfiguration?.categories || [];

  const { listings, fetched, inProgress } = useFeaturedListings(sectionId, options, allSections);

  // Carousel-ready: today we render the first deal only. Replace this slice
  // with the full `listings` array once a carousel UI is added.
  const visibleListings = listings.slice(0, 1);

  if (fetched && !inProgress && visibleListings.length === 0) {
    return null;
  }

  const hasTitle = title?.content;
  const hasDescription = description?.content;

  return (
    <section id={sectionId} className={classNames('bg-background py-16', className)}>
      <div className="container mx-auto max-w-7xl px-4">
        {hasTitle || hasDescription ? (
          <div className="mb-8 space-y-2">
            {hasTitle ? (
              <h2 className="font-heading text-3xl font-bold uppercase md:text-4xl">
                {title.content}
              </h2>
            ) : null}
            {hasDescription ? (
              <p className="text-sm uppercase tracking-[0.22em] text-muted-foreground md:text-base">
                {description.content}
              </p>
            ) : null}
          </div>
        ) : null}

        {visibleListings.map(listing => (
          <SpotlightDealCard
            key={listing?.id?.uuid}
            listing={listing}
            categories={categories}
          />
        ))}
      </div>
    </section>
  );
};

export default SpotlightDealSection;
